/**
 * Merit & Compensation Planning (PRF-9 / PRF-29) — one flat router, `meritContract` (`/merit`).
 *
 * WHAT THIS OWNS, AND WHERE IT STOPS. HR authors an increment matrix per review cycle (rating band
 * x optional pay-band position -> increment %, a total budget, an effective date). At cycle close,
 * `apps/worker`'s `merit-cycle-trigger.ts` — reusing its OWN existing eligibility scan, never a
 * second one — writes one `MeritRecommendation` per qualifying review, looked up against the
 * cycle's published matrix. A reporting manager edits and submits their own reporting-tree scope of
 * recommendations (`merit:submit`). Once the worksheets are in, HR submits the cycle for approval
 * (`submitMeritForApproval`, merit:manage — remediation plan R4/Q17): ONE `merit_cycle_budget`
 * `WorkflowInstance` covering every submitted row; the trigger no longer starts one at close.
 * `decideCycleBudget` acts on that instance's TERMINAL outcome only: approval promotes each covered
 * row into a `MeritCompensationProposal` (effective on the matrix date); rejection supersedes the
 * instance and returns the rows to `pending`. Rows submitted after an approval are covered by a fresh
 * instance the next time HR submits. There is no second, merit-specific approval mechanism.
 * This contract never writes a `Compensation` row: payroll applies an approved proposal through
 * `payroll-merit.contract.ts` (`applyMeritProposal`, compensation:update), the same internal function
 * a manual revision uses.
 *
 * GUARDRAILS (decision Q2): a manager may set any percentage, but more than ±2 percentage points
 * (`MERIT_DEVIATION_TOLERANCE_PP`) from the matrix needs a `managerComment` (422 otherwise) and the
 * row is reported `deviationFlagged`. The budget is Σ(proposed − current) annualised per pay basis in
 * the matrix currency; going over is a warning at submit and needs an `org_owner` step plus an
 * `overrideReason` at approval. A row whose currency differs from the matrix currency is refused
 * (422) at submit.
 *
 * SCOPE ENFORCEMENT: `getWorksheet`/`submitWorksheet`/`listProposals` resolve the caller's OWN
 * person-scope via
 * `resolveRosterPersonScope` (`apps/api/src/lib/person-scope.ts`) against the `merit:submit`/
 * `merit:approve` grants they hold — a manager who only holds a `reporting_tree`-scoped
 * `merit:submit` never sees or edits a row outside their own tree, and holding no qualifying grant
 * at all is a 403, not an empty list (see that route's own comment).
 *
 * PERMISSIONS, verbatim per the product decision this PR implements: `merit:read`, `merit:submit`,
 * `merit:approve`, `merit:reject`, `merit:manage` (never `review.manager:submit` or
 * `review.cycle:manage` for anything merit-shaped). Granting these to specific roles is
 * `packages/shared/src/permissions/roles.ts` — the Roles team's call, not this PR's.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  MERIT_MATRIX_STATUSES,
  MERIT_PROPOSAL_STATES,
  MERIT_RECOMMENDATION_STATUSES,
  PAY_BAND_POSITIONS,
} from '../enums.js';
import { payBasisSchema } from '../schemas/pay-basis.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  moneySchema,
  orgIdSchema,
  percentSchema,
  ratingValueSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const meritMatrixStatusSchema = z.enum(MERIT_MATRIX_STATUSES);
export const payBandPositionSchema = z.enum(PAY_BAND_POSITIONS);
export const meritRecommendationStatusSchema = z.enum(MERIT_RECOMMENDATION_STATUSES);
export const meritProposalStateSchema = z.enum(MERIT_PROPOSAL_STATES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** One increment-matrix cell — a rating band at an optional pay-band position -> increment %.
 * `ratingBand` is free text matching a label from the cycle's own `ReviewCycle.ratingBands`, the
 * same "typed Json on the cycle, free text on the child row" convention `Competency
 * .expectedLevelByGrade` already uses. `payBandPosition: null` applies regardless of position — the
 * default for an org that has not opted into that second dimension. */
export const meritMatrixCellSchema = z.object({
  ratingBand: z.string().min(1).max(60),
  payBandPosition: payBandPositionSchema.nullable(),
  /** Decimal string — invariant 2, even though this is a rate rather than money outright: it
   * multiplies directly into a Decimal compensation amount and must round exactly the same way. */
  incrementPercent: percentSchema,
});
export type MeritMatrixCell = z.infer<typeof meritMatrixCellSchema>;

export const meritMatrixSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  cycleId: uuidSchema,
  status: meritMatrixStatusSchema,
  totalBudget: moneySchema,
  effectiveDate: dateOnlySchema,
  cells: z.array(meritMatrixCellSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type MeritMatrix = z.infer<typeof meritMatrixSchema>;

export const saveMeritMatrixBodySchema = z.object({
  legalEntityId: uuidSchema.nullable().optional(),
  totalBudget: moneySchema,
  effectiveDate: dateOnlySchema,
  /** The WHOLE cell set, replacing whatever the matrix currently has — same "send the full child
   * collection" convention `review-cycle.contract.ts`'s `ratingBands` uses, so a removed band is
   * expressed by simply leaving it out rather than a separate delete call. Duplicate
   * `(ratingBand, payBandPosition)` pairs are a 422 at the route (see this contract's header on why
   * the DB itself cannot enforce that for a nullable position). */
  cells: z.array(meritMatrixCellSchema).max(200),
});
export type SaveMeritMatrixBody = z.infer<typeof saveMeritMatrixBodySchema>;

export const meritRecommendationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  cycleId: uuidSchema,
  matrixId: uuidSchema.nullable(),
  reviewId: uuidSchema,
  personId: uuidSchema,
  /** Denormalized for the worksheet grid — never a second round trip per row. */
  personName: z.string(),
  employeeCode: z.string().nullable(),
  employmentId: uuidSchema,
  managerPersonId: uuidSchema.nullable(),
  legalEntityId: uuidSchema.nullable(),
  finalRating: ratingValueSchema,
  ratingBand: z.string().nullable(),
  payBandPosition: payBandPositionSchema.nullable(),
  matrixIncrementPercent: percentSchema,
  /** The worksheet's editable "your %" cell. */
  proposedIncrementPercent: percentSchema,
  promotionRecommended: z.boolean(),
  currentCompensation: moneySchema,
  proposedCompensation: moneySchema,
  /** The basis `currentCompensation`/`proposedCompensation` are expressed on (ADR 0015): `ctc` is an
   * annual figure, `gross`/`net` monthly. Copied from the compensation row at generation (N12). */
  payBasis: payBasisSchema,
  /** The `Compensation` row the increment applies to — payroll refuses to apply a proposal once a
   * different row is in force (N12). Null on rows generated before this field existed. */
  currentCompensationId: uuidSchema.nullable(),
  status: meritRecommendationStatusSchema,
  managerComment: z.string().nullable(),
  /** `proposedIncrementPercent` is more than ±2 points from `matrixIncrementPercent` (Q2). Always
   * carries a `managerComment`; the approver sees the flag. */
  deviationFlagged: z.boolean(),
  submittedAt: instantSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  decidedBy: uuidSchema.nullable(),
  /** The `merit_cycle_budget` instance that covers (or last covered) this row. */
  workflowInstanceId: uuidSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type MeritRecommendation = z.infer<typeof meritRecommendationSchema>;

/** Cost vs budget for one cycle's matrix, over whatever recommendation set the caller may see —
 * the FULL cycle for HR, the caller's own reporting-tree scope for a manager (same scoping
 * `getWorksheet` applies, computed over the same rows so the two screens never disagree). */
export const meritBudgetSummarySchema = z.object({
  cycleId: uuidSchema,
  matrixId: uuidSchema.nullable(),
  totalBudget: moneySchema.nullable(),
  /** Σ `proposedCompensation − currentCompensation` over every recommendation in scope, regardless
   * of worksheet status, ANNUALISED per pay basis (ctc as is, gross/net ×12) and in the matrix
   * currency — "budget used". A manager watches it move as they type. Rows in another currency are
   * left out and counted in `currencyMismatchCount`. */
  proposedCost: moneySchema,
  /** The same sum over `approved` rows only — what is already committed. */
  approvedCost: moneySchema,
  /** `totalBudget − proposedCost`; null without a matrix. Negative when over budget. */
  remainingBudget: moneySchema.nullable(),
  /** `proposedCost > totalBudget` — a warning at submit; approval then needs the owner step (Q15). */
  overBudget: z.boolean(),
  recommendationCount: z.number().int().nonnegative(),
  submittedCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  deviationFlaggedCount: z.number().int().nonnegative(),
  currencyMismatchCount: z.number().int().nonnegative(),
});
export type MeritBudgetSummary = z.infer<typeof meritBudgetSummarySchema>;

/** The cycle's live `merit_cycle_budget` instance, once HR has submitted the cycle for approval. */
export const meritApprovalSummarySchema = z.object({
  workflowInstanceId: uuidSchema,
  /** pending | approved (a rejected instance is superseded at once and no longer live). */
  status: z.string(),
  /** Frozen at submission: whether the covered cost exceeded the budget (adds the owner step). */
  overBudget: z.boolean(),
  coveredCount: z.number().int().nonnegative(),
  startedAt: instantSchema,
});
export type MeritApprovalSummary = z.infer<typeof meritApprovalSummarySchema>;

export const meritWorksheetResponseSchema = z.object({
  recommendations: z.array(meritRecommendationSchema),
  budget: meritBudgetSummarySchema,
  /** Whether the caller's OWN scope has anything left unsubmitted — drives the worksheet's
   * Submit button; computed server-side so the web/mobile clients never re-derive it differently. */
  hasUnsubmitted: z.boolean(),
  /** Null until HR submits the cycle for approval, and again after a rejection. */
  approval: meritApprovalSummarySchema.nullable(),
});
export type MeritWorksheetResponse = z.infer<typeof meritWorksheetResponseSchema>;

export const updateMeritRecommendationBodySchema = z.object({
  proposedIncrementPercent: percentSchema.optional(),
  promotionRecommended: z.boolean().optional(),
  managerComment: z.string().max(2000).nullable().optional(),
});
export type UpdateMeritRecommendationBody = z.infer<typeof updateMeritRecommendationBodySchema>;

export const submitMeritWorksheetBodySchema = z.object({});

export const decideMeritCycleBudgetBodySchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  comment: z.string().max(2000).nullable().optional(),
  /** REQUIRED to approve an instance submitted over budget (Q2) — recorded with the decision on the
   * workflow step, which the audit trigger captures. Ignored on a rejection. */
  overrideReason: z.string().trim().min(1).max(2000).optional(),
});
export type DecideMeritCycleBudgetBody = z.infer<typeof decideMeritCycleBudgetBodySchema>;

export const submitMeritForApprovalBodySchema = z.object({});

/** A recommendation still `pending` when HR submitted the cycle — not covered, listed as a warning. */
export const meritPendingWarningSchema = z.object({
  recommendationId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  managerPersonId: uuidSchema.nullable(),
});

export const submitMeritForApprovalResponseSchema = z.object({
  workflowInstanceId: uuidSchema,
  /** Submitted rows this instance decides. */
  coveredCount: z.number().int().positive(),
  /** Annualised covered + already-approved cost exceeds the budget: the owner step was added. */
  overBudget: z.boolean(),
  /** The instance this one replaced (a pending round re-submitted, or an approved round followed by
   * late submissions), if any. Its history is kept. */
  supersededInstanceId: uuidSchema.nullable(),
  warnings: z.object({
    pending: z.array(meritPendingWarningSchema),
    deviationFlaggedCount: z.number().int().nonnegative(),
  }),
  worksheet: meritWorksheetResponseSchema,
});
export type SubmitMeritForApprovalResponse = z.infer<typeof submitMeritForApprovalResponseSchema>;

export const cancelMeritProposalBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type CancelMeritProposalBody = z.infer<typeof cancelMeritProposalBodySchema>;

export const meritCompensationProposalSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  employmentId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  cycleId: uuidSchema,
  recommendationId: uuidSchema,
  /** The matrix effective date (N12) — never the approval date. */
  effectiveDate: dateOnlySchema,
  /** The basis `proposedCompensation` is on; the new Compensation row keeps it. */
  payBasis: payBasisSchema,
  proposedCompensation: moneySchema,
  incrementPercent: percentSchema,
  promotionRecommended: z.boolean(),
  approvalState: meritProposalStateSchema,
  /** `merit_cycle:<cycleId>` — see the model's own comment in schema.prisma. */
  sourceReference: z.string(),
  /** The `merit_cycle_budget` instance whose approval created this proposal. */
  workflowInstanceId: uuidSchema.nullable(),
  approvedBy: uuidSchema,
  approvedAt: instantSchema,
  /** Set once payroll applies it (`consumed`): the Compensation row it wrote. */
  compensationId: uuidSchema.nullable(),
  consumedAt: instantSchema.nullable(),
  /** The `arrears` payroll input created for months already paid at the old rate (Q8), if any. */
  arrearsInputId: uuidSchema.nullable(),
  cancelledAt: instantSchema.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: instantSchema,
});
export type MeritCompensationProposal = z.infer<typeof meritCompensationProposalSchema>;

// ---------------------------------------------------------------------------
// Contract — /merit
// ---------------------------------------------------------------------------

export const meritContract = c.router(
  {
    getMatrix: {
      method: 'GET',
      path: '/cycles/:cycleId/matrix',
      responses: {
        200: z.object({ matrix: meritMatrixSchema.nullable() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Get one review cycle's increment matrix, if HR has authored one",
      metadata: routeMeta('merit:read', { salaryPin: 'others' }),
    },

    saveMatrix: {
      method: 'PUT',
      path: '/cycles/:cycleId/matrix',
      body: saveMeritMatrixBodySchema,
      responses: {
        200: meritMatrixSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Create or replace the draft increment matrix for a cycle (bands x pay-band position -> %, budget, effective date) — merit:manage',
      metadata: routeMeta('merit:manage', { salaryPin: 'others' }),
    },

    publishMatrix: {
      method: 'POST',
      path: '/cycles/:cycleId/matrix/publish',
      body: z.object({}),
      responses: {
        200: meritMatrixSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Publish the matrix — only a published matrix is read at cycle close (merit-cycle-trigger.ts)',
      metadata: routeMeta('merit:manage', { salaryPin: 'others' }),
    },

    getWorksheet: {
      method: 'GET',
      path: '/cycles/:cycleId/worksheet',
      responses: {
        200: meritWorksheetResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "The caller's merit worksheet for a cycle — their own reporting-tree scope for a manager, every recommendation for HR — merit:read",
      // Salary-bearing (current pay, proposed increase, new pay): kept out of the AI copilot's tool
      // list so no compensation figure is ever pulled into a prompt (remediation plan P1.1, R3).
      metadata: routeMeta('merit:read', { ai: 'hidden', salaryPin: 'others' }),
    },

    updateRecommendation: {
      method: 'PATCH',
      path: '/recommendations/:recommendationId',
      body: updateMeritRecommendationBodySchema,
      responses: {
        200: meritRecommendationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Edit one recommendation on the caller's own worksheet (your %, promotion flag, comment) — merit:submit",
      metadata: routeMeta('merit:submit', { salaryPin: 'others' }),
    },

    submitWorksheet: {
      method: 'POST',
      path: '/cycles/:cycleId/worksheet/submit',
      body: submitMeritWorksheetBodySchema,
      responses: {
        200: meritWorksheetResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        "Submit every pending recommendation in the caller's own scope for this cycle — merit:submit",
      metadata: routeMeta('merit:submit', { salaryPin: 'others' }),
    },

    decideCycleBudget: {
      method: 'POST',
      path: '/cycles/:cycleId/decision',
      body: decideMeritCycleBudgetBodySchema,
      responses: {
        200: meritWorksheetResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** Not submitted for approval yet, or no step is awaiting a decision. */
        412: errorBodySchema,
        /** Approving an over-budget round without `overrideReason`. */
        422: errorBodySchema,
      },
      summary:
        "HR's decision on the cycle's merit_cycle_budget WorkflowInstance — rows change only when the instance finishes: approval turns every covered submitted recommendation into a MeritCompensationProposal effective on the matrix date; rejection supersedes the instance and returns the rows to pending. An over-budget instance needs overrideReason to approve. merit:approve to approve, merit:reject to reject; never a second approval mechanism (see this contract's header)",
      metadata: routeMeta(['merit:approve', 'merit:reject'], { salaryPin: 'others' }),
    },

    submitMeritForApproval: {
      method: 'POST',
      path: '/cycles/:cycleId/approval',
      body: submitMeritForApprovalBodySchema,
      responses: {
        200: submitMeritForApprovalResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Nothing submitted to cover. */
        409: errorBodySchema,
        /** No active merit_cycle_budget workflow, or no approver could be resolved. */
        412: errorBodySchema,
        /** No published matrix, or a submitted row in another currency than the matrix. */
        422: errorBodySchema,
      },
      summary:
        'HR submits the cycle for ONE merit_cycle_budget approval covering every submitted recommendation (pending ones come back as warnings; over budget adds the owner step). Re-submitting supersedes a pending round, or after an approval starts a fresh round covering the late submissions only — merit:manage. Requires Idempotency-Key',
      metadata: routeMeta('merit:manage', { salaryPin: 'others' }),
    },

    cancelMeritProposal: {
      method: 'POST',
      path: '/proposals/:proposalId/cancel',
      body: cancelMeritProposalBodySchema,
      responses: {
        200: meritCompensationProposalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already applied by payroll (consumed) or already cancelled. */
        409: errorBodySchema,
      },
      summary:
        'Withdraw an approved, not-yet-applied compensation proposal (the reason is audited) — merit:manage. Requires Idempotency-Key',
      // Salary-bearing response, like listProposals below.
      metadata: routeMeta('merit:manage', { ai: 'hidden', salaryPin: 'others' }),
    },

    listProposals: {
      method: 'GET',
      path: '/cycles/:cycleId/proposals',
      responses: {
        200: z.object({ proposals: z.array(meritCompensationProposalSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "The effective-dated compensation proposals this cycle produced, limited to the caller's own scope exactly like the worksheet (a manager sees their tree's only) — merit:read. Payroll's queue is payroll-merit.contract.ts",
      // Salary-bearing, like getWorksheet above: never a copilot tool (remediation plan P1.1, R3).
      metadata: routeMeta('merit:read', { ai: 'hidden', salaryPin: 'others' }),
    },
  },
  { pathPrefix: '/merit' },
);
export type MeritContract = typeof meritContract;
